import { useState, useEffect } from "react";
import axios from "axios";
import { useChapters } from "@/hooks/useChapters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, Plus, Pencil, Trash2, Download, Users, Mail, Phone, MapPin, MessageCircle, UserPlus, FileText, UserCheck, Printer, ArrowUpCircle, BarChart3, CalendarDays, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Small chapter-filter chip row shown above the Prospects table. */
const ProspectChapterChips = ({ value, onChange, prospects }) => {
  const PROSPECT_CHAPTERS = useChapters().active.filter((c) => c !== "National");
  const countFor = (key) =>
    key === "all"
      ? prospects.length
      : prospects.filter((p) => (p.chapter || "") === key).length;
  const opts = [{ key: "all", label: "All" }, ...PROSPECT_CHAPTERS.map((k) => ({ key: k, label: k }))];
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="prospect-chapter-chips">
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            data-testid={`prospect-chapter-chip-${o.key}`}
            className={
              "px-3 py-1 rounded-full text-xs font-semibold border transition-colors " +
              (active
                ? "bg-cyan-500 text-slate-900 border-cyan-500"
                : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700")
            }
          >
            {o.label} <span className="opacity-70">({countFor(o.key)})</span>
          </button>
        );
      })}
    </div>
  );
};

// Helper function to get the Nth occurrence of a weekday in a month
const getNthWeekdayOfMonth = (year, month, weekday, n) => {
  const date = new Date(year, month, 1);
  let count = 0;
  
  while (date.getMonth() === month) {
    if (date.getDay() === weekday) {
      count++;
      if (count === n) {
        return new Date(date);
      }
    }
    date.setDate(date.getDate() + 1);
  }
  return null;
};

// Helper function to get 1st and 3rd Wednesday of each month
const getMeetingDates = (year) => {
  const dates = [];
  for (let month = 0; month < 12; month++) {
    const firstWed = getNthWeekdayOfMonth(year, month, 3, 1);
    const thirdWed = getNthWeekdayOfMonth(year, month, 3, 3);
    dates.push(firstWed, thirdWed);
  }
  return dates;
};

// Helper function to format date as MM/DD
const formatMeetingDate = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

export default function Prospects({ onLogout, userRole, userChapter }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [bulkPromoteDialogOpen, setBulkPromoteDialogOpen] = useState(false);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [promotingProspect, setPromotingProspect] = useState(null);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [prospectToDelete, setProspectToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [actionForm, setActionForm] = useState({ type: "merit", date: "", description: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [prospectChapterFilter, setProspectChapterFilter] = useState("all");
  const [meetingDates, setMeetingDates] = useState([]);
  const [attendanceExpanded, setAttendanceExpanded] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [addMeetingDialogOpen, setAddMeetingDialogOpen] = useState(false);
  const [newMeetingDate, setNewMeetingDate] = useState("");
  const [newMeetingStatus, setNewMeetingStatus] = useState(1);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [newMeetingNote, setNewMeetingNote] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const navigate = useNavigate();
  
  // Active tab state (hangarounds or prospects)
  const [activeTab, setActiveTab] = useState("hangarounds");
  
  // Hangaround state
  const [hangarounds, setHangarounds] = useState([]);
  const [hangaroundDialogOpen, setHangaroundDialogOpen] = useState(false);
  const [editingHangaround, setEditingHangaround] = useState(null);
  const [hangaroundToDelete, setHangaroundToDelete] = useState(null);
  const [hangaroundDeleteDialogOpen, setHangaroundDeleteDialogOpen] = useState(false);
  const [hangaroundDeleteReason, setHangaroundDeleteReason] = useState("");
  const [promoteToProspectDialogOpen, setPromoteToProspectDialogOpen] = useState(false);
  const [hangaroundToPromote, setHangaroundToPromote] = useState(null);
  const [hangaroundFormData, setHangaroundFormData] = useState({ handle: "", name: "", phone: "", email: "" });
  const [promoteToProspectFormData, setPromoteToProspectFormData] = useState({
    email: "",
    phone: "",
    address: "",
    chapter: "",
    dob: "",
    join_date: "",
    military_service: false,
    military_branch: "",
    is_first_responder: false
  });
  const [hangaroundSearchTerm, setHangaroundSearchTerm] = useState("");
  const [selectedHangaround, setSelectedHangaround] = useState(null);
  const [hangaroundActionsDialogOpen, setHangaroundActionsDialogOpen] = useState(false);
  const [hangaroundActionForm, setHangaroundActionForm] = useState({ type: "merit", date: "", description: "" });
  const [hangaroundAttendanceDialogOpen, setHangaroundAttendanceDialogOpen] = useState(false);
  const [hangaroundAttendanceExpanded, setHangaroundAttendanceExpanded] = useState(false);
  const [hangaroundSelectedYear, setHangaroundSelectedYear] = useState(new Date().getFullYear().toString());
  const [hangaroundAddMeetingDialogOpen, setHangaroundAddMeetingDialogOpen] = useState(false);
  const [hangaroundNewMeetingDate, setHangaroundNewMeetingDate] = useState("");
  const [hangaroundNewMeetingStatus, setHangaroundNewMeetingStatus] = useState(1);
  const [hangaroundNewMeetingNote, setHangaroundNewMeetingNote] = useState("");
  const [hangaroundAttendanceData, setHangaroundAttendanceData] = useState({});
  
  // Prospect Attendance State (quick access from list)
  const [prospectAttendanceDialogOpen, setProspectAttendanceDialogOpen] = useState(false);
  const [prospectAttendanceSelectedYear, setProspectAttendanceSelectedYear] = useState(new Date().getFullYear().toString());
  const [prospectAddMeetingDialogOpen, setProspectAddMeetingDialogOpen] = useState(false);
  const [prospectNewMeetingDate, setProspectNewMeetingDate] = useState("");
  const [prospectNewMeetingStatus, setProspectNewMeetingStatus] = useState(1);
  const [prospectNewMeetingNote, setProspectNewMeetingNote] = useState("");
  const [prospectAttendanceData, setProspectAttendanceData] = useState({});
  const [selectedProspectForAttendance, setSelectedProspectForAttendance] = useState(null);
  
  // Check if user can edit prospects - will be updated from API response
  // PM title has view-only access, so they cannot edit
  const userTitle = localStorage.getItem('title') || '';
  const storedPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "{}"); } catch { return {}; }
  })();
  const canEditProspects = userTitle !== 'PM' &&
                           (storedPermissions?.edit_prospects ||
                            (userRole === 'admin' && userChapter === 'National'));

  // Promote permission can be granted dynamically — derived from backend `can_promote` flag
  const canPromoteProspects = canEditProspects ||
                              prospects.some((p) => p && p.can_promote);

  const [formData, setFormData] = useState({
    handle: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    dob: "",
    join_date: "",
    military_service: false,
    military_branch: "",
    is_first_responder: false,
    meeting_attendance: {
      [new Date().getFullYear().toString()]: []
    }
  });

  const [promoteFormData, setPromoteFormData] = useState({
    chapter: "",
    title: "Brother"  // Prospects can only be promoted to Brother
  });

  useEffect(() => {
    fetchProspects();
    fetchHangarounds();
  }, []);

  useEffect(() => {
    const dates = getMeetingDates(formData.meeting_attendance.year);
    setMeetingDates(dates);
  }, [formData.meeting_attendance.year]);

  const fetchHangarounds = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/hangarounds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHangarounds(response.data);
    } catch (error) {
      console.error("Failed to load hangarounds:", error);
    }
  };

  const fetchProspects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/prospects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProspects(response.data);
    } catch (error) {
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      if (editingProspect) {
        await axios.put(
          `${API}/prospects/${editingProspect.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Prospect updated successfully");
      } else {
        await axios.post(`${API}/prospects`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Prospect added successfully");
      }
      fetchProspects();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save prospect");
    }
  };

  const handleEdit = (prospect) => {
    setEditingProspect(prospect);
    const currentYear = new Date().getFullYear().toString();
    
    // Handle meeting_attendance - support both old and new format
    let attendanceData = {};
    
    if (prospect.meeting_attendance) {
      if (prospect.meeting_attendance.year && prospect.meeting_attendance.meetings) {
        // Old format - convert to new flexible format
        const yearStr = prospect.meeting_attendance.year.toString();
        const meetings = prospect.meeting_attendance.meetings || [];
        attendanceData[yearStr] = meetings.map((m, idx) => {
          const monthIdx = Math.floor(idx / 2);
          const weekNum = (idx % 2) + 1;
          const approxDate = new Date(parseInt(yearStr), monthIdx, weekNum * 7);
          return {
            date: approxDate.toISOString().split('T')[0],
            status: typeof m === 'object' ? (m.status || 0) : (m || 0),
            note: typeof m === 'object' ? (m.note || '') : ''
          };
        }).filter(m => m.status !== 0 || m.note);
      } else {
        // New format - use as is
        attendanceData = { ...prospect.meeting_attendance };
      }
    }
    
    if (!attendanceData[currentYear]) {
      attendanceData[currentYear] = [];
    }

    setFormData({
      handle: prospect.handle,
      name: prospect.name,
      email: prospect.email,
      phone: prospect.phone,
      address: prospect.address,
      dob: prospect.dob || "",
      join_date: prospect.join_date || "",
      military_service: prospect.military_service || false,
      military_branch: prospect.military_branch || "",
      is_first_responder: prospect.is_first_responder || false,
      meeting_attendance: attendanceData
    });
    setDialogOpen(true);
  };

  const handleDelete = (prospect) => {
    setProspectToDelete(prospect);
    setDeleteReason("");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteReason.trim()) {
      toast.error("Please provide a reason for archiving");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/prospects/${prospectToDelete.id}`, {
        params: { reason: deleteReason },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Prospect archived successfully");
      setDeleteDialogOpen(false);
      setProspectToDelete(null);
      setDeleteReason("");
      fetchProspects();
    } catch (error) {
      toast.error("Failed to archive prospect");
    }
  };

  const handlePromote = (prospect) => {
    setPromotingProspect(prospect);
    setPromoteFormData({
      // Pre-fill the chapter with whatever the Prospect was assigned to,
      // so officers don't re-pick it during the final promotion to Brother.
      chapter: prospect.chapter || "",
      title: "Brother"  // Prospects can only be promoted to Brother
    });
    setPromoteDialogOpen(true);
  };

  const handlePromoteSubmit = async (e) => {
    e.preventDefault();
    
    if (!promoteFormData.chapter || !promoteFormData.title) {
      toast.error("Please select both chapter and title");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/prospects/${promotingProspect.id}/promote`,
        null,
        {
          params: {
            chapter: promoteFormData.chapter,
            title: promoteFormData.title
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`${promotingProspect.handle} promoted to member successfully!`);
      const q = response.data?.questionnaire;
      if (q?.dm_sent) {
        toast.success("Member Profile Questionnaire link sent via Discord DM (expires in 72 hours)");
      } else if (q?.link) {
        try { await navigator.clipboard.writeText(q.link); } catch (e) { /* clipboard unavailable */ }
        toast.warning(
          `Discord DM could not be sent (${q?.dm_error || "unknown error"}). Questionnaire link copied to clipboard — share it manually.`,
          { duration: 10000 }
        );
      }
      setPromoteDialogOpen(false);
      setPromotingProspect(null);
      fetchProspects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote prospect");
    }
  };

  const handleToggleSelect = (prospectId) => {
    setSelectedProspects(prev => 
      prev.includes(prospectId) 
        ? prev.filter(id => id !== prospectId)
        : [...prev, prospectId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProspects.length === filteredProspects.length) {
      setSelectedProspects([]);
    } else {
      setSelectedProspects(filteredProspects.map(p => p.id));
    }
  };

  const handleBulkPromote = () => {
    if (selectedProspects.length === 0) {
      toast.error("Please select prospects to promote");
      return;
    }
    setPromoteFormData({ chapter: "", title: "Brother" });
    setBulkPromoteDialogOpen(true);
  };

  const handleBulkPromoteSubmit = async (e) => {
    e.preventDefault();
    
    if (!promoteFormData.chapter || !promoteFormData.title) {
      toast.error("Please select both chapter and title");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/prospects/bulk-promote`,
        {
          prospect_ids: selectedProspects,
          chapter: promoteFormData.chapter,
          title: promoteFormData.title
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Successfully promoted ${response.data.promoted_count} prospects!`);
      if (response.data.questionnaire_dms_sent > 0) {
        toast.success(`Questionnaire links DM'd to ${response.data.questionnaire_dms_sent} new Brother(s) via Discord`);
      }
      if (response.data.failed_count > 0) {
        toast.error(`Failed to promote ${response.data.failed_count} prospects`);
      }
      
      setBulkPromoteDialogOpen(false);
      setSelectedProspects([]);
      fetchProspects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk promote prospects");
    }
  };

  const handleOpenActions = (prospect) => {
    setSelectedProspect(prospect);
    setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
    setActionsDialogOpen(true);
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    if (!actionForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API}/prospects/${selectedProspect.id}/actions`,
        null,
        {
          params: {
            action_type: actionForm.type,
            date: actionForm.date,
            description: actionForm.description
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Action added successfully");
      setActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
      fetchProspects(); // Refresh to get updated actions
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add action");
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm("Are you sure you want to delete this action?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/prospects/${selectedProspect.id}/actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Action deleted successfully");
      fetchProspects(); // Refresh to get updated actions
    } catch (error) {
      toast.error("Failed to delete action");
    }
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear().toString();
    setFormData({
      handle: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      dob: "",
      join_date: "",
      military_service: false,
      military_branch: "",
      is_first_responder: false,
      meeting_attendance: {
        [currentYear]: []
      }
    });
    setEditingProspect(null);
  };

  const handleAttendanceToggle = (index) => {
    const yearMeetings = formData.meeting_attendance[selectedYear] || [];
    const newMeetings = [...yearMeetings];
    const currentStatus = newMeetings[index]?.status || 0;
    
    newMeetings[index] = {
      ...newMeetings[index],
      status: (currentStatus + 1) % 3
    };
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [selectedYear]: newMeetings
      }
    });
  };

  const handleAttendanceNoteChange = (index, note) => {
    const yearMeetings = formData.meeting_attendance[selectedYear] || [];
    const newMeetings = [...yearMeetings];
    
    newMeetings[index] = {
      ...newMeetings[index],
      note: note
    };
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [selectedYear]: newMeetings
      }
    });
  };

  const handleAddMeeting = () => {
    if (!newMeetingDate) {
      toast.error("Please select a date");
      return;
    }
    
    const meetingYear = newMeetingDate.split('-')[0];
    const yearMeetings = formData.meeting_attendance[meetingYear] || [];
    
    if (yearMeetings.some(m => m.date === newMeetingDate)) {
      toast.error("A meeting already exists for this date");
      return;
    }
    
    const newMeeting = {
      date: newMeetingDate,
      status: newMeetingStatus,
      note: newMeetingNote
    };
    
    const newMeetings = [...yearMeetings, newMeeting].sort((a, b) => a.date.localeCompare(b.date));
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [meetingYear]: newMeetings
      }
    });
    
    setNewMeetingDate("");
    setNewMeetingStatus(1);
    setNewMeetingNote("");
    setAddMeetingDialogOpen(false);
    toast.success("Meeting added");
  };

  const handleDeleteMeeting = (meetingIndex) => {
    const yearMeetings = formData.meeting_attendance[selectedYear] || [];
    const newMeetings = yearMeetings.filter((_, idx) => idx !== meetingIndex);
    
    setFormData({
      ...formData,
      meeting_attendance: {
        ...formData.meeting_attendance,
        [selectedYear]: newMeetings
      }
    });
    toast.success("Meeting removed");
  };

  const formatMeetingDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/prospects/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'prospects.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  // Print Custom functions
  const fetchCSVData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/prospects/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse CSV text into array
      const text = response.data;
      const rows = [];
      let row = [];
      let cell = '';
      let inQuotes = false;
      
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          row.push(cell.trim());
          cell = '';
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
          if (cell || row.length > 0) {
            row.push(cell.trim());
            if (row.some(c => c)) rows.push(row);
            row = [];
            cell = '';
          }
        } else {
          cell += char;
        }
      }
      if (cell || row.length > 0) {
        row.push(cell.trim());
        if (row.some(c => c)) rows.push(row);
      }
      
      setCsvData(rows);
    } catch (error) {
      console.error("Failed to fetch CSV data:", error);
    }
  };

  const openPrintModal = async () => {
    await fetchCSVData();
    setShowPrintModal(true);
  };

  const selectPreset = (preset) => {
    if (!csvData[0]) return;
    
    const headers = csvData[0].map(h => h.toLowerCase());
    let columns = [];
    
    switch (preset) {
      case 'all':
        columns = headers.map((_, i) => i);
        break;
      case 'contact':
        columns = headers.map((h, i) => 
          ['handle', 'name', 'email', 'phone', 'address'].some(c => h.includes(c)) ? i : -1
        ).filter(i => i !== -1);
        break;
      case 'service':
        columns = headers.map((h, i) => 
          ['handle', 'name', 'military', 'first responder', 'branch'].some(c => h.includes(c)) ? i : -1
        ).filter(i => i !== -1);
        break;
      case 'attendance':
        // Select attendance summary columns
        columns = headers.map((h, i) => 
          ['handle', 'name', 'attendance year', 'total meetings', 'present', 'excused', 'absent', 'attendance %', 'meeting details'].some(c => h.includes(c)) ? i : -1
        ).filter(i => i !== -1);
        break;
      default:
        break;
    }
    
    setSelectedColumns(columns);
  };

  const printSelectedColumns = () => {
    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column");
      return;
    }
    
    const filteredData = csvData.map(row => 
      selectedColumns.map(colIndex => row[colIndex] || '')
    );
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Prospects Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #4a5568; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            @media print { 
              body { margin: 0; }
              table { font-size: 10px; }
            }
          </style>
        </head>
        <body>
          <h2>Prospects Report</h2>
          <table>
            <thead>
              <tr>${filteredData[0]?.map(h => `<th>${h}</th>`).join('') || ''}</tr>
            </thead>
            <tbody>
              ${filteredData.slice(1).map(row => 
                `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`
              ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ==================== HANGAROUND HANDLERS ====================
  
  const handleAddHangaround = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    
    try {
      if (editingHangaround) {
        await axios.put(
          `${API}/hangarounds/${editingHangaround.id}`,
          hangaroundFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Hangaround updated successfully");
      } else {
        await axios.post(`${API}/hangarounds`, hangaroundFormData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Hangaround added successfully");
      }
      fetchHangarounds();
      setHangaroundDialogOpen(false);
      setHangaroundFormData({ handle: "", name: "", phone: "", email: "" });
      setEditingHangaround(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save hangaround");
    }
  };

  const handleEditHangaround = (hangaround) => {
    setEditingHangaround(hangaround);
    setHangaroundFormData({
      handle: hangaround.handle,
      name: hangaround.name,
      phone: hangaround.phone || "",
      email: hangaround.email || ""
    });
    setHangaroundDialogOpen(true);
  };

  const handleDeleteHangaround = (hangaround) => {
    setHangaroundToDelete(hangaround);
    setHangaroundDeleteReason("");
    setHangaroundDeleteDialogOpen(true);
  };

  const handleConfirmDeleteHangaround = async () => {
    if (!hangaroundDeleteReason.trim()) {
      toast.error("Please provide a reason for archiving");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/hangarounds/${hangaroundToDelete.id}`, {
        params: { reason: hangaroundDeleteReason },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Hangaround archived successfully");
      setHangaroundDeleteDialogOpen(false);
      setHangaroundToDelete(null);
      setHangaroundDeleteReason("");
      fetchHangarounds();
    } catch (error) {
      toast.error("Failed to archive hangaround");
    }
  };

  const handleOpenPromoteToProspect = (hangaround) => {
    setHangaroundToPromote(hangaround);
    setPromoteToProspectFormData({
      email: "",
      phone: "",
      address: "",
      chapter: "",
      dob: "",
      join_date: "",
      military_service: false,
      military_branch: "",
      is_first_responder: false
    });
    setPromoteToProspectDialogOpen(true);
  };

  const handlePromoteToProspect = async (e) => {
    e.preventDefault();
    
    if (!promoteToProspectFormData.chapter) {
      toast.error("Please select a Chapter for this Prospect");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API}/hangarounds/${hangaroundToPromote.id}/promote`,
        promoteToProspectFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${hangaroundToPromote.handle} promoted to Prospect successfully!`);
      setPromoteToProspectDialogOpen(false);
      setHangaroundToPromote(null);
      fetchHangarounds();
      fetchProspects();
      setActiveTab("prospects"); // Switch to prospects tab to see the newly promoted prospect
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote to prospect");
    }
  };

  const handleOpenHangaroundActions = (hangaround) => {
    setSelectedHangaround(hangaround);
    setHangaroundActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
    setHangaroundActionsDialogOpen(true);
  };

  const handleAddHangaroundAction = async (e) => {
    e.preventDefault();
    if (!hangaroundActionForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API}/hangarounds/${selectedHangaround.id}/actions`,
        null,
        {
          params: {
            action_type: hangaroundActionForm.type,
            date: hangaroundActionForm.date,
            description: hangaroundActionForm.description
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Action added successfully");
      setHangaroundActionForm({ type: "merit", date: new Date().toISOString().split('T')[0], description: "" });
      fetchHangarounds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add action");
    }
  };

  const handleDeleteHangaroundAction = async (actionId) => {
    if (!window.confirm("Are you sure you want to delete this action?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/hangarounds/${selectedHangaround.id}/actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Action deleted successfully");
      fetchHangarounds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete action");
    }
  };

  // Hangaround Attendance Handlers
  const handleOpenHangaroundAttendance = (hangaround) => {
    setSelectedHangaround(hangaround);
    const currentYear = new Date().getFullYear().toString();
    let attendanceData = {};
    
    if (hangaround.meeting_attendance) {
      attendanceData = { ...hangaround.meeting_attendance };
    }
    
    if (!attendanceData[currentYear]) {
      attendanceData[currentYear] = [];
    }
    
    setHangaroundAttendanceData(attendanceData);
    setHangaroundSelectedYear(currentYear);
    setHangaroundAttendanceDialogOpen(true);
  };

  const handleHangaroundAttendanceToggle = (index) => {
    const yearMeetings = hangaroundAttendanceData[hangaroundSelectedYear] || [];
    const meeting = yearMeetings[index];
    if (!meeting) return;
    
    const currentStatus = meeting.status || 0;
    const newStatus = currentStatus === 0 ? 1 : currentStatus === 1 ? 2 : 0;
    
    const updatedMeetings = [...yearMeetings];
    updatedMeetings[index] = { ...meeting, status: newStatus };
    
    setHangaroundAttendanceData({
      ...hangaroundAttendanceData,
      [hangaroundSelectedYear]: updatedMeetings
    });
  };

  const handleHangaroundAttendanceNoteChange = (index, note) => {
    const yearMeetings = hangaroundAttendanceData[hangaroundSelectedYear] || [];
    const updatedMeetings = [...yearMeetings];
    if (updatedMeetings[index]) {
      updatedMeetings[index] = { ...updatedMeetings[index], note };
    }
    
    setHangaroundAttendanceData({
      ...hangaroundAttendanceData,
      [hangaroundSelectedYear]: updatedMeetings
    });
  };

  const handleHangaroundAddMeeting = () => {
    if (!hangaroundNewMeetingDate) {
      toast.error("Please select a date");
      return;
    }
    
    const meetingYear = hangaroundNewMeetingDate.split('-')[0];
    const yearMeetings = hangaroundAttendanceData[meetingYear] || [];
    
    // Check for duplicate date
    if (yearMeetings.some(m => m.date === hangaroundNewMeetingDate)) {
      toast.error("A meeting already exists for this date");
      return;
    }
    
    const newMeeting = {
      date: hangaroundNewMeetingDate,
      status: parseInt(hangaroundNewMeetingStatus),
      note: hangaroundNewMeetingNote
    };
    
    const updatedMeetings = [...yearMeetings, newMeeting].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    setHangaroundAttendanceData({
      ...hangaroundAttendanceData,
      [meetingYear]: updatedMeetings
    });
    
    setHangaroundAddMeetingDialogOpen(false);
    setHangaroundNewMeetingDate("");
    setHangaroundNewMeetingStatus(1);
    setHangaroundNewMeetingNote("");
    setHangaroundSelectedYear(meetingYear);
  };

  const handleHangaroundDeleteMeeting = (index) => {
    const yearMeetings = hangaroundAttendanceData[hangaroundSelectedYear] || [];
    const updatedMeetings = yearMeetings.filter((_, i) => i !== index);
    
    setHangaroundAttendanceData({
      ...hangaroundAttendanceData,
      [hangaroundSelectedYear]: updatedMeetings
    });
  };

  const handleSaveHangaroundAttendance = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API}/hangarounds/${selectedHangaround.id}`,
        { meeting_attendance: hangaroundAttendanceData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Attendance saved successfully");
      fetchHangarounds();
      setHangaroundAttendanceDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save attendance");
    }
  };

  const getHangaroundAttendanceSummary = (hangaround) => {
    const currentYear = new Date().getFullYear().toString();
    const meetings = hangaround.meeting_attendance?.[currentYear] || [];
    const total = meetings.length;
    const present = meetings.filter(m => m?.status === 1).length;
    const excused = meetings.filter(m => m?.status === 2).length;
    return { total, present, excused };
  };

  // Prospect Attendance Summary (for list display)
  const getProspectAttendanceSummary = (prospect) => {
    const currentYear = new Date().getFullYear().toString();
    const meetings = prospect.meeting_attendance?.[currentYear] || [];
    const total = meetings.length;
    const present = meetings.filter(m => m?.status === 1).length;
    const excused = meetings.filter(m => m?.status === 2).length;
    return { total, present, excused };
  };

  // Prospect Attendance Handlers (quick access from list)
  const handleOpenProspectAttendance = (prospect) => {
    setSelectedProspectForAttendance(prospect);
    const currentYear = new Date().getFullYear().toString();
    let attendanceData = {};
    
    if (prospect.meeting_attendance) {
      attendanceData = { ...prospect.meeting_attendance };
    }
    if (!attendanceData[currentYear]) {
      attendanceData[currentYear] = [];
    }
    
    setProspectAttendanceData(attendanceData);
    setProspectAttendanceSelectedYear(currentYear);
    setProspectAttendanceDialogOpen(true);
  };

  const handleProspectAttendanceToggle = (index) => {
    const yearMeetings = prospectAttendanceData[prospectAttendanceSelectedYear] || [];
    const meeting = yearMeetings[index];
    if (!meeting) return;
    
    const currentStatus = meeting.status || 0;
    const newStatus = currentStatus === 1 ? 2 : currentStatus === 2 ? 0 : 1;
    const updatedMeetings = [...yearMeetings];
    updatedMeetings[index] = { ...meeting, status: newStatus };
    
    setProspectAttendanceData({
      ...prospectAttendanceData,
      [prospectAttendanceSelectedYear]: updatedMeetings
    });
  };

  const handleProspectAttendanceNoteChange = (index, note) => {
    const yearMeetings = prospectAttendanceData[prospectAttendanceSelectedYear] || [];
    const updatedMeetings = [...yearMeetings];
    updatedMeetings[index] = { ...updatedMeetings[index], note };
    
    setProspectAttendanceData({
      ...prospectAttendanceData,
      [prospectAttendanceSelectedYear]: updatedMeetings
    });
  };

  const handleProspectAddMeeting = () => {
    if (!prospectNewMeetingDate) {
      toast.error("Please select a meeting date");
      return;
    }
    
    const meetingYear = prospectNewMeetingDate.split('-')[0];
    const yearMeetings = prospectAttendanceData[meetingYear] || [];
    
    // Check for duplicates
    if (yearMeetings.some(m => m.date === prospectNewMeetingDate)) {
      toast.error("A meeting already exists for this date");
      return;
    }
    
    const newMeeting = {
      date: prospectNewMeetingDate,
      status: prospectNewMeetingStatus,
      note: prospectNewMeetingNote || ""
    };
    
    const updatedMeetings = [...yearMeetings, newMeeting].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    setProspectAttendanceData({
      ...prospectAttendanceData,
      [meetingYear]: updatedMeetings
    });
    
    setProspectAddMeetingDialogOpen(false);
    setProspectNewMeetingDate("");
    setProspectNewMeetingStatus(1);
    setProspectNewMeetingNote("");
    setProspectAttendanceSelectedYear(meetingYear);
  };

  const handleProspectDeleteMeeting = (meetingIndex) => {
    const yearMeetings = prospectAttendanceData[prospectAttendanceSelectedYear] || [];
    const newMeetings = yearMeetings.filter((_, idx) => idx !== meetingIndex);
    
    setProspectAttendanceData({
      ...prospectAttendanceData,
      [prospectAttendanceSelectedYear]: newMeetings
    });
  };

  const handleSaveProspectAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/prospects/${selectedProspectForAttendance.id}`, 
        { meeting_attendance: prospectAttendanceData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Attendance saved successfully");
      fetchProspects();
      setProspectAttendanceDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save attendance");
    }
  };

  const filteredHangarounds = hangarounds.filter((hangaround) => {
    const search = hangaroundSearchTerm.toLowerCase();
    return (
      hangaround.name.toLowerCase().includes(search) ||
      hangaround.handle.toLowerCase().includes(search)
    );
  });

  const filteredProspects = prospects.filter((prospect) => {
    const search = searchTerm.toLowerCase();
    const chapterOk =
      prospectChapterFilter === "all" ||
      (prospect.chapter || "") === prospectChapterFilter;
    if (!chapterOk) return false;
    return (
      prospect.name.toLowerCase().includes(search) ||
      prospect.handle.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const headerActions = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs sm:text-sm text-slate-300">
        {localStorage.getItem("username")} ({userRole})
      </span>
      {/* Prospect Channel Analytics - only for specific titles */}
      {userRole === 'admin' && ['Prez', 'VP', 'S@A', 'ENF', 'SEC'].includes(userTitle) && (
        <Button
          onClick={() => navigate("/prospect-channel-analytics")}
          variant="outline"
          size="sm"
          className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm bg-blue-700 text-white border-blue-600 hover:bg-blue-600"
        >
          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Channel Analytics</span>
        </Button>
      )}
      {userRole === 'admin' && (
        <Button
          onClick={() => navigate("/users")}
          variant="outline"
          size="sm"
          className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
        >
          <Users className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Admin</span>
        </Button>
      )}
      <Button
        onClick={onLogout}
        variant="outline"
        size="sm"
        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
      >
        <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
        Logout
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Hangarounds & Prospects"
      icon={UserCheck}
      backTo="/"
      backLabel="Members"
      actions={headerActions}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-700">
          <TabsTrigger 
            value="hangarounds" 
            className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"
            data-testid="hangarounds-tab"
          >
            Hangarounds ({hangarounds.length})
          </TabsTrigger>
          <TabsTrigger 
            value="prospects" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            data-testid="prospects-tab"
          >
            Prospects ({prospects.length})
          </TabsTrigger>
        </TabsList>

        {/* ==================== HANGAROUNDS TAB ==================== */}
        <TabsContent value="hangarounds">
          <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
            <div className="mb-4 sm:mb-6">
              <div className="relative max-w-2xl">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <Input
                  placeholder="Search hangarounds by name or handle..."
                  value={hangaroundSearchTerm}
                  onChange={(e) => setHangaroundSearchTerm(e.target.value)}
                  className="w-full pl-10 py-4 sm:py-6 text-sm sm:text-base bg-slate-900 border-2 border-slate-700 text-slate-100 focus:border-slate-600 rounded-lg placeholder:text-white"
                  data-testid="hangaround-search-input"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="flex gap-2 flex-wrap">
                {canEditProspects && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const link = `${window.location.origin}/hangaround-intake`;
                      navigator.clipboard.writeText(link).then(
                        () => toast.success("Intake link copied — send it to prospective Hangarounds"),
                        () => toast.info(link, { duration: 10000 })
                      );
                    }}
                    className="flex items-center justify-center gap-2 border-amber-700 text-amber-400 hover:bg-amber-900/30"
                    data-testid="copy-intake-link-btn"
                  >
                    <Link2 className="w-4 h-4" />
                    Copy Intake Link
                  </Button>
                )}
                {canEditProspects && (
                  <Dialog open={hangaroundDialogOpen} onOpenChange={(open) => {
                    setHangaroundDialogOpen(open);
                    if (!open) {
                      setHangaroundFormData({ handle: "", name: "", phone: "", email: "" });
                      setEditingHangaround(null);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="add-hangaround-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add Hangaround
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[95vw] sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl">
                          {editingHangaround ? "Edit Hangaround" : "Add New Hangaround"}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddHangaround} className="space-y-4 mt-4">
                        <div>
                          <Label>Handle *</Label>
                          <Input
                            value={hangaroundFormData.handle}
                            onChange={(e) => setHangaroundFormData({ ...hangaroundFormData, handle: e.target.value })}
                            required
                            placeholder="Enter handle/nickname"
                            data-testid="hangaround-handle-input"
                          />
                        </div>
                        <div>
                          <Label>Name (optional)</Label>
                          <Input
                            value={hangaroundFormData.name}
                            onChange={(e) => setHangaroundFormData({ ...hangaroundFormData, name: e.target.value })}
                            placeholder="Enter full name"
                            data-testid="hangaround-name-input"
                          />
                        </div>
                        <div>
                          <Label>Phone (optional)</Label>
                          <Input
                            value={hangaroundFormData.phone}
                            onChange={(e) => setHangaroundFormData({ ...hangaroundFormData, phone: e.target.value })}
                            placeholder="(555) 555-5555"
                            data-testid="hangaround-phone-input"
                          />
                        </div>
                        <div>
                          <Label>Email (optional)</Label>
                          <Input
                            value={hangaroundFormData.email}
                            onChange={(e) => setHangaroundFormData({ ...hangaroundFormData, email: e.target.value })}
                            placeholder="you@example.com"
                            data-testid="hangaround-email-input"
                          />
                        </div>
                        <p className="text-sm text-slate-400">
                          Only a Handle is required for Hangarounds. Additional info will be collected later via the Member Profile Questionnaire.
                        </p>
                        <div className="flex gap-3 justify-end pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setHangaroundDialogOpen(false);
                              setHangaroundFormData({ handle: "", name: "", phone: "", email: "" });
                              setEditingHangaround(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="save-hangaround-btn">
                            {editingHangaround ? "Update" : "Add"} Hangaround
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            {/* Hangarounds Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-300">Handle</TableHead>
                    <TableHead className="text-slate-300">Name</TableHead>
                    <TableHead className="text-slate-300">Phone</TableHead>
                    <TableHead className="text-slate-300">Email</TableHead>
                    <TableHead className="text-slate-300">Attendance</TableHead>
                    <TableHead className="text-slate-300">Actions</TableHead>
                    <TableHead className="text-slate-300 text-right">Operations</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHangarounds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-400 py-8">
                        No hangarounds found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHangarounds.map((hangaround) => {
                      const attendance = getHangaroundAttendanceSummary(hangaround);
                      return (
                      <TableRow key={hangaround.id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell className="text-white font-medium">{hangaround.handle}</TableCell>
                        <TableCell className="text-slate-300">{hangaround.name}</TableCell>
                        <TableCell className="text-slate-300 whitespace-nowrap">{hangaround.phone ? <a href={`tel:${hangaround.phone}`} className="hover:text-amber-400">{hangaround.phone}</a> : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell className="text-slate-300">{hangaround.email ? <a href={`mailto:${hangaround.email}`} className="hover:text-amber-400">{hangaround.email}</a> : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenHangaroundAttendance(hangaround)}
                            className="text-slate-300 hover:text-white hover:bg-slate-600 p-1 h-auto"
                          >
                            {attendance.total > 0 ? (
                              <div className="flex gap-1 items-center text-xs">
                                <span className="text-green-400">{attendance.present}P</span>
                                <span className="text-orange-400">{attendance.excused}E</span>
                                <span className="text-slate-400">/ {attendance.total}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">No meetings</span>
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {hangaround.actions?.filter(a => a.type === 'merit').length > 0 && (
                              <span className="px-2 py-0.5 bg-green-600/20 text-green-400 rounded text-xs">
                                {hangaround.actions.filter(a => a.type === 'merit').length} Merit
                              </span>
                            )}
                            {hangaround.actions?.filter(a => a.type === 'disciplinary').length > 0 && (
                              <span className="px-2 py-0.5 bg-red-600/20 text-red-400 rounded text-xs">
                                {hangaround.actions.filter(a => a.type === 'disciplinary').length} Disc
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end flex-wrap">
                            {canEditProspects && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenPromoteToProspect(hangaround)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                                  data-testid={`promote-hangaround-${hangaround.id}`}
                                >
                                  <ArrowUpCircle className="w-4 h-4 mr-1" />
                                  Promote to Prospect
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenHangaroundActions(hangaround)}
                                  className="bg-slate-600 hover:bg-slate-700 text-white border-slate-600"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditHangaround(hangaround)}
                                  className="bg-slate-600 hover:bg-slate-700 text-white border-slate-600"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteHangaround(hangaround)}
                                  className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ==================== PROSPECTS TAB ==================== */}
        <TabsContent value="prospects">
        <div className="bg-slate-800 rounded-xl shadow-sm border border-slate-700 p-4 sm:p-6">
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder="Search by name or handle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 py-4 sm:py-6 text-sm sm:text-base bg-slate-900 border-2 border-slate-700 text-slate-100 focus:border-slate-600 rounded-lg placeholder:text-white"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleExportCSV}
                size="sm"
                className="flex items-center justify-center gap-2 text-xs sm:text-sm bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>

              <Button
                onClick={openPrintModal}
                size="sm"
                className="flex items-center justify-center gap-2 text-xs sm:text-sm bg-purple-700 text-white hover:bg-purple-800"
              >
                <Printer className="w-4 h-4" />
                Print Custom
              </Button>

              {selectedProspects.length > 0 && canPromoteProspects && (
                <Button
                  onClick={handleBulkPromote}
                  size="sm"
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-promote-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Bulk Promote ({selectedProspects.length})
                </Button>
              )}

              {canEditProspects && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white"
                  >
                    <Plus className="w-4 h-4" />
                    Add Prospect
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl">
                    {editingProspect ? "Edit Prospect" : "Add New Prospect"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Handle *</Label>
                      <Input
                        value={formData.handle}
                        onChange={(e) => setFormData({ ...formData, handle: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Name (optional)</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Email (optional)</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone (optional)</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Address (optional)</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label>Date of Birth (optional)</Label>
                    <Input
                      type="date"
                      value={formData.dob}
                      onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label>Anniversary Date (MM/YYYY)</Label>
                    <Input
                      type="text"
                      placeholder="MM/YYYY"
                      value={formData.join_date}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d/]/g, '');
                        if (value.length === 2 && !value.includes('/') && formData.join_date.length < value.length) {
                          value = value + '/';
                        }
                        if (value.length <= 7) {
                          setFormData({ ...formData, join_date: value });
                        }
                      }}
                    />
                  </div>

                  {/* Military Service Section */}
                  <div className="space-y-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                    <Label className="text-white font-semibold">🎖️ Military Service</Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="prospect_military_service"
                        checked={formData.military_service || false}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, military_service: checked, military_branch: checked ? formData.military_branch : "" })
                        }
                      />
                      <label htmlFor="prospect_military_service" className="text-sm text-slate-200 cursor-pointer">
                        Served in Military
                      </label>
                    </div>
                    {formData.military_service && (
                      <div className="ml-6">
                        <Label className="text-slate-300 text-sm">Branch of Service</Label>
                        <Select
                          value={formData.military_branch || ""}
                          onValueChange={(value) => setFormData({ ...formData, military_branch: value })}
                        >
                          <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                            <SelectValue placeholder="Select branch" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            <SelectItem value="Army" className="text-white hover:bg-slate-700">Army</SelectItem>
                            <SelectItem value="Navy" className="text-white hover:bg-slate-700">Navy</SelectItem>
                            <SelectItem value="Air Force" className="text-white hover:bg-slate-700">Air Force</SelectItem>
                            <SelectItem value="Marines" className="text-white hover:bg-slate-700">Marines</SelectItem>
                            <SelectItem value="Coast Guard" className="text-white hover:bg-slate-700">Coast Guard</SelectItem>
                            <SelectItem value="Space Force" className="text-white hover:bg-slate-700">Space Force</SelectItem>
                            <SelectItem value="National Guard" className="text-white hover:bg-slate-700">National Guard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* First Responder Section */}
                  <div className="space-y-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
                    <Label className="text-white font-semibold">🚨 First Responder Service</Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="prospect_is_first_responder"
                        checked={formData.is_first_responder || false}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, is_first_responder: checked })
                        }
                      />
                      <label htmlFor="prospect_is_first_responder" className="text-sm text-slate-200 cursor-pointer">
                        Served as First Responder (Police, Fire, or EMS)
                      </label>
                    </div>
                  </div>

                  {/* Collapsible Meeting Attendance Section */}
                  <div className="border border-slate-600 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setAttendanceExpanded(!attendanceExpanded)}
                      className="w-full flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Label className="cursor-pointer text-base font-semibold">Meeting Attendance ({selectedYear})</Label>
                        {/* Summary counts */}
                        {(() => {
                          const yearMeetings = formData.meeting_attendance[selectedYear] || [];
                          const total = yearMeetings.length;
                          const present = yearMeetings.filter(m => m?.status === 1).length;
                          const excused = yearMeetings.filter(m => m?.status === 2).length;
                          const absent = yearMeetings.filter(m => m?.status === 0).length;
                          return (
                            <div className="flex gap-2 text-xs">
                              <span className="px-2 py-0.5 bg-slate-600 text-white rounded">{total} total</span>
                              <span className="px-2 py-0.5 bg-green-600 text-white rounded">{present} P</span>
                              <span className="px-2 py-0.5 bg-orange-500 text-white rounded">{excused} E</span>
                              <span className="px-2 py-0.5 bg-red-600/80 text-white rounded">{absent} A</span>
                            </div>
                          );
                        })()}
                      </div>
                      <svg 
                        className={`w-5 h-5 text-slate-400 transition-transform ${attendanceExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {attendanceExpanded && (
                      <div className="p-3 space-y-3 bg-slate-800/50">
                        {/* Year Selector and Add Meeting Button */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">Year:</span>
                            <select
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(e.target.value)}
                              className="bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1"
                            >
                              {[new Date().getFullYear().toString(), ...Object.keys(formData.meeting_attendance).filter(k => k !== new Date().getFullYear().toString())].sort((a, b) => b - a).map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setAddMeetingDialogOpen(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-xs h-7"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Meeting
                          </Button>
                        </div>
                        
                        {/* Legend */}
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Click to cycle status</span>
                          <div className="flex gap-2">
                            <span className="text-green-500">● Present</span>
                            <span className="text-orange-500">● Excused</span>
                            <span className="text-red-500">● Absent</span>
                          </div>
                        </div>
                        
                        {/* Flexible Meetings List */}
                        {(() => {
                          const yearMeetings = formData.meeting_attendance[selectedYear] || [];
                          
                          if (yearMeetings.length === 0) {
                            return (
                              <div className="text-center py-4 text-slate-500 text-sm">
                                No meetings recorded for {selectedYear}
                                <p className="text-xs mt-1">Click &quot;Add Meeting&quot; to add one</p>
                              </div>
                            );
                          }
                          
                          return (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {yearMeetings.map((meeting, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-2 p-2 bg-slate-900/50 rounded"
                                >
                                  <span className="text-xs text-slate-300 w-20 flex-shrink-0">
                                    {formatMeetingDate(meeting.date)}
                                  </span>
                                  
                                  <button
                                    type="button"
                                    onClick={() => handleAttendanceToggle(idx)}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                                      meeting.status === 1
                                        ? 'bg-green-600 text-white'
                                        : meeting.status === 2
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-red-600/80 text-white'
                                    }`}
                                  >
                                    {meeting.status === 1 ? 'Present' : meeting.status === 2 ? 'Excused' : 'Absent'}
                                  </button>
                                  
                                  <Input
                                    type="text"
                                    value={meeting.note || ''}
                                    onChange={(e) => handleAttendanceNoteChange(idx, e.target.value)}
                                    placeholder="Note..."
                                    className="flex-1 text-xs h-7 bg-slate-800 border-slate-600 text-white"
                                  />
                                  
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteMeeting(idx)}
                                    className="text-red-400 hover:text-red-300 p-1"
                                    title="Remove meeting"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Add Meeting Dialog */}
                  <Dialog open={addMeetingDialogOpen} onOpenChange={setAddMeetingDialogOpen}>
                    <DialogContent className="bg-slate-800 border-slate-600 max-w-sm">
                      <DialogHeader>
                        <DialogTitle className="text-white">Add Meeting</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-slate-200">Date</Label>
                          <Input
                            type="date"
                            value={newMeetingDate}
                            onChange={(e) => setNewMeetingDate(e.target.value)}
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-200">Status</Label>
                          <Select value={newMeetingStatus.toString()} onValueChange={(v) => setNewMeetingStatus(parseInt(v))}>
                            <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              <SelectItem value="1" className="text-white hover:bg-slate-700">Present</SelectItem>
                              <SelectItem value="2" className="text-white hover:bg-slate-700">Excused</SelectItem>
                              <SelectItem value="0" className="text-white hover:bg-slate-700">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-200">Note (optional)</Label>
                          <Input
                            value={newMeetingNote}
                            onChange={(e) => setNewMeetingNote(e.target.value)}
                            placeholder="e.g., reason for absence"
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setAddMeetingDialogOpen(false)}
                            className="text-white border-slate-600"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={handleAddMeeting}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Add Meeting
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="text-white"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-slate-800 hover:bg-slate-900 text-white">
                      {editingProspect ? "Update" : "Save"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
            </div>
          </div>

          {filteredProspects.length === 0 ? (
            <>
              <ProspectChapterChips
                value={prospectChapterFilter}
                onChange={setProspectChapterFilter}
                prospects={prospects}
              />
              <p className="text-center text-slate-400 py-8">No prospects found</p>
            </>
          ) : (
            <div className="overflow-x-auto">
              <ProspectChapterChips
                value={prospectChapterFilter}
                onChange={setProspectChapterFilter}
                prospects={prospects}
              />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedProspects.length === filteredProspects.length && filteredProspects.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                        className="border-slate-400 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                      />
                    </TableHead>
                    <TableHead>Handle</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Attendance</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProspects.map((prospect) => (
                    <TableRow key={prospect.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProspects.includes(prospect.id)}
                          onCheckedChange={() => handleToggleSelect(prospect.id)}
                          aria-label={`Select ${prospect.handle}`}
                          className="border-slate-400 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                        />
                      </TableCell>
                      <TableCell className="text-white">{prospect.handle}</TableCell>
                      <TableCell className="text-white">{prospect.name}</TableCell>
                      <TableCell>
                        {prospect.chapter ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-cyan-600/20 text-cyan-300 border border-cyan-700/50">
                            {prospect.chapter}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500 italic">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const attendance = getProspectAttendanceSummary(prospect);
                          return (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenProspectAttendance(prospect)}
                              className="text-slate-300 hover:text-white hover:bg-slate-600 p-1 h-auto"
                              title="Click to view/edit attendance"
                              data-testid={`prospect-attendance-${prospect.id}`}
                            >
                              {attendance.total > 0 ? (
                                <div className="flex gap-1 items-center text-xs">
                                  <span className="text-green-400">{attendance.present}P</span>
                                  <span className="text-orange-400">{attendance.excused}E</span>
                                  <span className="text-slate-400">/ {attendance.total}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-500">No meetings</span>
                              )}
                            </Button>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${prospect.email}`}
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                        >
                          <Mail className="w-3 h-3" />
                          {prospect.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        <a
                          href={`tel:${prospect.phone}`}
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline text-sm"
                        >
                          <Phone className="w-3 h-3" />
                          {prospect.phone}
                        </a>
                      </TableCell>
                      <TableCell>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(prospect.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-white hover:text-slate-300 hover:underline text-sm"
                        >
                          <MapPin className="w-3 h-3" />
                          {prospect.address}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          {/* View Actions - Always visible */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-500 hover:text-blue-600 hover:bg-blue-950/50 h-8 w-8 p-0"
                            onClick={() => handleOpenActions(prospect)}
                            title="View Actions"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          
                          {/* Promote Action — gated by can_promote (admins or granted permission) */}
                          {prospect.can_promote && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600 hover:text-green-700 hover:bg-green-950/50 h-8 w-8 p-0"
                              onClick={() => handlePromote(prospect)}
                              title="Promote to Member"
                              data-testid={`promote-prospect-${prospect.id}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Edit/Delete buttons only visible if user can edit */}
                          {prospect.can_edit && (
                            <>
                              {/* Edit Action */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-300 hover:text-white hover:bg-slate-600 h-8 w-8 p-0"
                                onClick={() => handleEdit(prospect)}
                                title="Edit Prospect"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              
                              {/* Delete Action */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-600 hover:bg-red-950/50 h-8 w-8 p-0"
                                onClick={() => handleDelete(prospect)}
                                title="Archive Prospect"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Promote Dialog */}
        <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Promote to Member</DialogTitle>
            </DialogHeader>
            {promotingProspect && (
              <form onSubmit={handlePromoteSubmit} className="space-y-4 mt-4">
                <div className="bg-slate-100 p-3 rounded-md">
                  <p className="text-sm text-slate-600">Promoting:</p>
                  <p className="font-semibold text-slate-900">{promotingProspect.handle} - {promotingProspect.name}</p>
                </div>

                <div>
                  <Label>Chapter *</Label>
                  <Select
                    value={promoteFormData.chapter}
                    onValueChange={(value) =>
                      setPromoteFormData({ ...promoteFormData, chapter: value })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select chapter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AD">AD - Asphalt Demons</SelectItem>
                      <SelectItem value="HS">HS - Highway Souls</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Title</Label>
                  <div
                    className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-slate-100"
                    data-testid="promote-title-readonly"
                  >
                    <UserPlus className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Brother</span>
                    <span className="text-xs text-slate-500 ml-auto">Prospects are always promoted to Brother</span>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPromoteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Promote to Member
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Actions Dialog */}
        <Dialog open={actionsDialogOpen} onOpenChange={setActionsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Actions History - {selectedProspect?.handle}</DialogTitle>
            </DialogHeader>
            {selectedProspect && (
              <div className="space-y-6 mt-4">
                {/* Existing Actions List */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">History</h3>
                  {selectedProspect.actions && selectedProspect.actions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedProspect.actions.sort((a, b) => new Date(b.date) - new Date(a.date)).map((action) => (
                        <div key={action.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                action.type === 'merit' ? 'bg-green-600 text-white' :
                                action.type === 'promotion' ? 'bg-blue-600 text-white' :
                                'bg-red-600 text-white'
                              }`}>
                                {action.type.toUpperCase()}
                              </span>
                              <span className="text-slate-400 text-sm">{action.date}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteAction(action.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-slate-200 text-sm mb-2">{action.description}</p>
                          <p className="text-slate-400 text-xs">
                            Added by {action.added_by} on {new Date(action.added_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">No actions recorded yet.</p>
                  )}
                </div>

                {/* Add New Action Form */}
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Add New Action</h3>
                  <form onSubmit={handleAddAction} className="space-y-4">
                    <div>
                      <Label>Type</Label>
                      <Select
                        value={actionForm.type}
                        onValueChange={(value) => setActionForm({ ...actionForm, type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="merit">Merit</SelectItem>
                          <SelectItem value="promotion">Promotion</SelectItem>
                          <SelectItem value="disciplinary">Disciplinary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={actionForm.date}
                        onChange={(e) => setActionForm({ ...actionForm, date: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={actionForm.description}
                        onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                        placeholder="Enter action details..."
                        rows={4}
                        required
                      />
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setActionsDialogOpen(false)}
                      >
                        Close
                      </Button>
                      <Button type="submit">Add Action</Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Archive Prospect</DialogTitle>
            </DialogHeader>
            {prospectToDelete && (
              <div className="space-y-4 mt-4">
                <p className="text-slate-200">
                  You are about to archive <span className="font-semibold">{prospectToDelete.handle} - {prospectToDelete.name}</span>. 
                  This action will move the prospect to the archived records.
                </p>
                <div>
                  <Label>Reason for Archiving *</Label>
                  <Textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Enter reason for archiving..."
                    rows={3}
                    required
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmDelete}
                  >
                    Archive Prospect
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Promote Dialog */}
        <Dialog open={bulkPromoteDialogOpen} onOpenChange={setBulkPromoteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bulk Promote to Members</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBulkPromoteSubmit} className="space-y-4 mt-4">
              <div className="bg-slate-100 p-3 rounded-md">
                <p className="text-sm text-slate-600">Promoting {selectedProspects.length} prospects</p>
              </div>

              <div>
                <Label>Chapter *</Label>
                <Select
                  value={promoteFormData.chapter}
                  onValueChange={(value) =>
                    setPromoteFormData({ ...promoteFormData, chapter: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select chapter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AD">AD - Asphalt Demons</SelectItem>
                    <SelectItem value="HS">HS - Highway Souls</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Title</Label>
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-slate-100"
                  data-testid="bulk-promote-title-readonly"
                >
                  <UserPlus className="w-4 h-4 text-green-600" />
                  <span className="font-medium">Brother</span>
                  <span className="text-xs text-slate-500 ml-auto">Prospects are always promoted to Brother</span>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBulkPromoteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Promote {selectedProspects.length} to Members
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Print Custom Modal */}
        {showPrintModal && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowPrintModal(false)}>
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-purple-400 mb-3 sm:mb-4">
                Print Custom - Prospects
              </h2>
              
              <div className="mb-3 sm:mb-4">
                <p className="text-slate-300 mb-2 sm:mb-3 text-sm sm:text-base">Select which columns to include:</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <button onClick={() => selectPreset('all')} className="bg-blue-600 hover:bg-blue-700 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-white">All Fields</button>
                  <button onClick={() => selectPreset('contact')} className="bg-purple-600 hover:bg-purple-700 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-white">Contact</button>
                  <button onClick={() => selectPreset('service')} className="bg-indigo-600 hover:bg-indigo-700 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-white">🎖️ Service</button>
                  <button onClick={() => selectPreset('attendance')} className="bg-green-600 hover:bg-green-700 px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-white">Attendance</button>
                  <button onClick={() => setSelectedColumns([])} className="bg-slate-600 hover:bg-slate-700 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-white">Clear</button>
                </div>
              </div>

              <div className="bg-slate-700 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 max-h-48 sm:max-h-64 overflow-y-auto">
                {csvData[0]?.map((header, index) => (
                  <div key={index} className="flex items-center gap-3 py-2 hover:bg-slate-600 px-2 rounded">
                    <input
                      type="checkbox"
                      id={`prospect-col-${index}`}
                      checked={selectedColumns.includes(index)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns([...selectedColumns, index]);
                        } else {
                          setSelectedColumns(selectedColumns.filter(i => i !== index));
                        }
                      }}
                      className="w-5 h-5 accent-purple-600"
                    />
                    <label htmlFor={`prospect-col-${index}`} className="cursor-pointer flex-1 text-white">
                      {header}
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="flex-1 bg-slate-600 hover:bg-slate-700 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={printSelectedColumns}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base text-white"
                >
                  Print Selected
                </button>
              </div>
            </div>
          </div>
        )}
        </TabsContent>
      </Tabs>

      {/* ==================== HANGAROUND DIALOGS ==================== */}
      
      {/* Delete Hangaround Confirmation Dialog */}
      <Dialog open={hangaroundDeleteDialogOpen} onOpenChange={setHangaroundDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Hangaround</DialogTitle>
          </DialogHeader>
          {hangaroundToDelete && (
            <div className="space-y-4 mt-4">
              <p className="text-slate-200">
                You are about to archive <span className="font-semibold">{hangaroundToDelete.handle} - {hangaroundToDelete.name}</span>. 
                This action will move the hangaround to the archived records.
              </p>
              <div>
                <Label>Reason for Archiving *</Label>
                <Textarea
                  value={hangaroundDeleteReason}
                  onChange={(e) => setHangaroundDeleteReason(e.target.value)}
                  placeholder="Enter reason for archiving..."
                  rows={3}
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setHangaroundDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDeleteHangaround}
                >
                  Archive Hangaround
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Promote Hangaround to Prospect Dialog */}
      <Dialog open={promoteToProspectDialogOpen} onOpenChange={setPromoteToProspectDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Promote to Prospect: {hangaroundToPromote?.handle}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePromoteToProspect} className="space-y-4 mt-4">
            <p className="text-sm text-slate-400 mb-4">
              Select the chapter this Prospect will be assigned to. Their profile
              details are collected later via the Member Profile Questionnaire when
              they're promoted to Brother.
            </p>

            <div>
              <Label>Chapter *</Label>
              <Select
                value={promoteToProspectFormData.chapter}
                onValueChange={(value) =>
                  setPromoteToProspectFormData({ ...promoteToProspectFormData, chapter: value })
                }
                required
              >
                <SelectTrigger data-testid="promote-hangaround-chapter">
                  <SelectValue placeholder="Select chapter for this Prospect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AD">AD - Asphalt Demons</SelectItem>
                  <SelectItem value="HS">HS - Highway Souls</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPromoteToProspectDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-promote-to-prospect-btn"
              >
                Promote to Prospect
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hangaround Actions Dialog */}
      <Dialog open={hangaroundActionsDialogOpen} onOpenChange={setHangaroundActionsDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Actions - {selectedHangaround?.handle}
            </DialogTitle>
          </DialogHeader>
          {selectedHangaround && (
            <div className="space-y-6 mt-4">
              {/* Existing Actions List */}
              {(hangarounds.find(h => h.id === selectedHangaround.id)?.actions || []).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Action History</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(hangarounds.find(h => h.id === selectedHangaround.id)?.actions || []).map((action) => (
                      <div 
                        key={action.id} 
                        className={`p-3 rounded-lg border ${
                          action.type === 'merit' ? 'bg-green-900/20 border-green-700' :
                          action.type === 'disciplinary' ? 'bg-red-900/20 border-red-700' :
                          'bg-blue-900/20 border-blue-700'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span className={`text-xs font-semibold uppercase ${
                              action.type === 'merit' ? 'text-green-400' :
                              action.type === 'disciplinary' ? 'text-red-400' :
                              'text-blue-400'
                            }`}>
                              {action.type}
                            </span>
                            <span className="text-slate-400 text-xs ml-2">{action.date}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                            onClick={() => handleDeleteHangaroundAction(action.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-slate-200 text-sm mt-1">{action.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Action Form */}
              <div className="border-t border-slate-700 pt-4">
                <Label className="text-base font-semibold mb-3 block">Add New Action</Label>
                <form onSubmit={handleAddHangaroundAction} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Action Type</Label>
                      <Select
                        value={hangaroundActionForm.type}
                        onValueChange={(value) => setHangaroundActionForm({ ...hangaroundActionForm, type: value })}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="merit" className="text-green-400">Merit</SelectItem>
                          <SelectItem value="promotion" className="text-blue-400">Promotion</SelectItem>
                          <SelectItem value="disciplinary" className="text-red-400">Disciplinary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={hangaroundActionForm.date}
                        onChange={(e) => setHangaroundActionForm({ ...hangaroundActionForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={hangaroundActionForm.description}
                      onChange={(e) => setHangaroundActionForm({ ...hangaroundActionForm, description: e.target.value })}
                      placeholder="Enter action details..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setHangaroundActionsDialogOpen(false)}
                    >
                      Close
                    </Button>
                    <Button type="submit">Add Action</Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hangaround Attendance Dialog */}
      <Dialog open={hangaroundAttendanceDialogOpen} onOpenChange={setHangaroundAttendanceDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Meeting Attendance - {selectedHangaround?.handle}
            </DialogTitle>
          </DialogHeader>
          {selectedHangaround && (
            <div className="space-y-4 mt-4">
              {/* Year Selector and Add Meeting Button */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Year:</span>
                  <select
                    value={hangaroundSelectedYear}
                    onChange={(e) => setHangaroundSelectedYear(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-2 py-1"
                  >
                    {[new Date().getFullYear().toString(), ...Object.keys(hangaroundAttendanceData).filter(k => k !== new Date().getFullYear().toString() && k.match(/^\d{4}$/))].sort((a, b) => b - a).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                {canEditProspects && (
                  <Dialog open={hangaroundAddMeetingDialogOpen} onOpenChange={setHangaroundAddMeetingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Meeting
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add Meeting</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Meeting Date</Label>
                          <Input
                            type="date"
                            value={hangaroundNewMeetingDate}
                            onChange={(e) => setHangaroundNewMeetingDate(e.target.value)}
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <Label>Status</Label>
                          <Select
                            value={hangaroundNewMeetingStatus.toString()}
                            onValueChange={(value) => setHangaroundNewMeetingStatus(parseInt(value))}
                          >
                            <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              <SelectItem value="1" className="text-white hover:bg-slate-700">Present</SelectItem>
                              <SelectItem value="2" className="text-white hover:bg-slate-700">Excused</SelectItem>
                              <SelectItem value="0" className="text-white hover:bg-slate-700">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Note (optional)</Label>
                          <Input
                            value={hangaroundNewMeetingNote}
                            onChange={(e) => setHangaroundNewMeetingNote(e.target.value)}
                            placeholder="e.g., reason for absence"
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setHangaroundAddMeetingDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={handleHangaroundAddMeeting}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Add Meeting
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Attendance Summary */}
              {(() => {
                const yearMeetings = hangaroundAttendanceData[hangaroundSelectedYear] || [];
                const total = yearMeetings.length;
                const present = yearMeetings.filter(m => m?.status === 1).length;
                const excused = yearMeetings.filter(m => m?.status === 2).length;
                const absent = yearMeetings.filter(m => m?.status === 0).length;
                return (
                  <div className="flex gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-slate-600 text-white rounded">{total} total</span>
                    <span className="px-2 py-0.5 bg-green-600 text-white rounded">{present} Present</span>
                    <span className="px-2 py-0.5 bg-orange-500 text-white rounded">{excused} Excused</span>
                    <span className="px-2 py-0.5 bg-red-600/80 text-white rounded">{absent} Absent</span>
                  </div>
                );
              })()}

              {/* Legend */}
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Click status to cycle: Present → Excused → Absent</span>
              </div>

              {/* Meetings List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(hangaroundAttendanceData[hangaroundSelectedYear] || []).length === 0 ? (
                  <p className="text-center text-slate-400 py-4">No meetings recorded for {hangaroundSelectedYear}</p>
                ) : (
                  (hangaroundAttendanceData[hangaroundSelectedYear] || []).map((meeting, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-700/50 rounded-lg">
                      <span className="text-sm text-slate-300 w-24">{meeting.date}</span>
                      <button
                        type="button"
                        onClick={() => canEditProspects && handleHangaroundAttendanceToggle(idx)}
                        className={`w-20 py-1 rounded text-xs font-medium ${
                          meeting.status === 1 ? 'bg-green-600 text-white' :
                          meeting.status === 2 ? 'bg-orange-500 text-white' :
                          'bg-red-600/80 text-white'
                        } ${canEditProspects ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      >
                        {meeting.status === 1 ? 'Present' : meeting.status === 2 ? 'Excused' : 'Absent'}
                      </button>
                      {canEditProspects ? (
                        <Input
                          value={meeting.note || ''}
                          onChange={(e) => handleHangaroundAttendanceNoteChange(idx, e.target.value)}
                          placeholder="Note..."
                          className="flex-1 h-8 text-sm bg-slate-700 border-slate-600 text-white"
                        />
                      ) : (
                        <span className="flex-1 text-sm text-slate-400">{meeting.note || '-'}</span>
                      )}
                      {canEditProspects && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleHangaroundDeleteMeeting(idx)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setHangaroundAttendanceDialogOpen(false)}
                >
                  Cancel
                </Button>
                {canEditProspects && (
                  <Button
                    type="button"
                    onClick={handleSaveHangaroundAttendance}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Save Attendance
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Prospect Attendance Dialog (Quick Access) */}
      <Dialog open={prospectAttendanceDialogOpen} onOpenChange={setProspectAttendanceDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl text-white">
              Meeting Attendance - {selectedProspectForAttendance?.handle}
            </DialogTitle>
          </DialogHeader>
          {selectedProspectForAttendance && (
            <div className="space-y-4 mt-4">
              {/* Year Selector and Add Meeting Button */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Year:</span>
                  <select
                    value={prospectAttendanceSelectedYear}
                    onChange={(e) => setProspectAttendanceSelectedYear(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-2 py-1"
                  >
                    {[new Date().getFullYear().toString(), ...Object.keys(prospectAttendanceData).filter(k => k !== new Date().getFullYear().toString() && k.match(/^\d{4}$/))].sort((a, b) => b - a).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                {canEditProspects && (
                  <Dialog open={prospectAddMeetingDialogOpen} onOpenChange={setProspectAddMeetingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-sm"
                        data-testid="add-prospect-meeting-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Meeting
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md bg-slate-800 border-slate-700">
                      <DialogHeader>
                        <DialogTitle className="text-white">Add Meeting</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="text-white">Meeting Date</Label>
                          <Input
                            type="date"
                            value={prospectNewMeetingDate}
                            onChange={(e) => setProspectNewMeetingDate(e.target.value)}
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white">Status</Label>
                          <Select
                            value={prospectNewMeetingStatus.toString()}
                            onValueChange={(value) => setProspectNewMeetingStatus(parseInt(value))}
                          >
                            <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              <SelectItem value="1" className="text-white hover:bg-slate-700">Present</SelectItem>
                              <SelectItem value="2" className="text-white hover:bg-slate-700">Excused</SelectItem>
                              <SelectItem value="0" className="text-white hover:bg-slate-700">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white">Note (optional)</Label>
                          <Input
                            value={prospectNewMeetingNote}
                            onChange={(e) => setProspectNewMeetingNote(e.target.value)}
                            placeholder="e.g., reason for absence"
                            className="mt-1 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        <div className="flex gap-3 justify-end pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setProspectAddMeetingDialogOpen(false)}
                            className="border-slate-600 text-white hover:bg-slate-700"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={handleProspectAddMeeting}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Add Meeting
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Attendance Summary */}
              {(() => {
                const yearMeetings = prospectAttendanceData[prospectAttendanceSelectedYear] || [];
                const total = yearMeetings.length;
                const present = yearMeetings.filter(m => m?.status === 1).length;
                const excused = yearMeetings.filter(m => m?.status === 2).length;
                const absent = yearMeetings.filter(m => m?.status === 0).length;
                return (
                  <div className="flex gap-2 text-sm flex-wrap">
                    <span className="px-2 py-0.5 bg-slate-600 text-white rounded">{total} total</span>
                    <span className="px-2 py-0.5 bg-green-600 text-white rounded">{present} Present</span>
                    <span className="px-2 py-0.5 bg-orange-500 text-white rounded">{excused} Excused</span>
                    <span className="px-2 py-0.5 bg-red-600/80 text-white rounded">{absent} Absent</span>
                  </div>
                );
              })()}

              {/* Legend */}
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Click status to cycle: Present → Excused → Absent</span>
              </div>

              {/* Meetings List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(prospectAttendanceData[prospectAttendanceSelectedYear] || []).length === 0 ? (
                  <p className="text-center text-slate-400 py-4">No meetings recorded for {prospectAttendanceSelectedYear}</p>
                ) : (
                  (prospectAttendanceData[prospectAttendanceSelectedYear] || []).map((meeting, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-700/50 rounded-lg">
                      <span className="text-sm text-slate-300 w-24">{meeting.date}</span>
                      <button
                        type="button"
                        onClick={() => canEditProspects && handleProspectAttendanceToggle(idx)}
                        className={`w-20 py-1 rounded text-xs font-medium ${
                          meeting.status === 1 ? 'bg-green-600 text-white' :
                          meeting.status === 2 ? 'bg-orange-500 text-white' :
                          'bg-red-600/80 text-white'
                        } ${canEditProspects ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      >
                        {meeting.status === 1 ? 'Present' : meeting.status === 2 ? 'Excused' : 'Absent'}
                      </button>
                      {canEditProspects ? (
                        <Input
                          value={meeting.note || ''}
                          onChange={(e) => handleProspectAttendanceNoteChange(idx, e.target.value)}
                          placeholder="Note..."
                          className="flex-1 h-8 text-sm bg-slate-700 border-slate-600 text-white"
                        />
                      ) : (
                        <span className="flex-1 text-sm text-slate-400">{meeting.note || '-'}</span>
                      )}
                      {canEditProspects && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleProspectDeleteMeeting(idx)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setProspectAttendanceDialogOpen(false)}
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
                {canEditProspects && (
                  <Button
                    type="button"
                    onClick={handleSaveProspectAttendance}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-prospect-attendance-btn"
                  >
                    Save Attendance
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
